import { AuthClientResponse, AuthScenario } from '@sola/schema/dotcom/auth.js';
import { Supporter } from '@sola/schema/dotcom/proxy-api/supporter.js';
import {
  sendCreateAccountEvent,
  sendLoginEvent,
} from '@sola/shared-utils/analytics-events.js';
import { httpPostSigned } from '@sola/shared-utils/httpClient.js';

// The NEXT_PUBLIC_BASE_URL_AUTH env value isn't required in the auth app
const apiPrefix =
  process.env.NEXT_PUBLIC_BASE_URL_AUTH || process.env.BASE_URL_AUTH || '';

const authEndpoints = {
  create: `${apiPrefix}/api/create`,
  createValidate: `${apiPrefix}/api/create-validate`,
  logIn: `${apiPrefix}/api/login`,
  logOut: `${apiPrefix}/api/logout`,
  password: `${apiPrefix}/api/password`,
  passwordValidate: `${apiPrefix}/api/password-validate`,
  resendConfirmation: `${apiPrefix}/api/resend-confirmation`,
  username: `${apiPrefix}/api/username`,
  verify: `${apiPrefix}/api/verify`,
};

const defaultApiOptions: RequestInit = {
  credentials: 'include',
};

// TODO: All of these interfaces for payload/request and responses seem like they could be shared so the SDK and API use the same...
interface AuthPayload {
  username: string;
  password: string;
  includeSupporter?: boolean;
  includePaymentMethods?: boolean;
}

export interface AuthSuccess {
  success: true;
  authUserGlobalId: string;
  authUserInternalId: string;
  authUserPrimaryContactId: string;
  authUsername: string;
  supporter?: Supporter;
  // Not sure I like this...
  sessionJwt?: string;
  type?: AuthScenario;
}

export interface AuthError {
  success: false;
  message: string;
  type: AuthScenario;
}

export type AuthResponse = AuthSuccess | AuthError | null | undefined;

/**
 * Authenticates the user, and stores initial auth user object in redis cache, and returns it.
 */
export const authenticateUser = async (
  payload: AuthPayload,
): Promise<AuthResponse> => {
  const authResponse = await httpPostSigned<AuthResponse>(
    authEndpoints.logIn,
    {
      ...payload,
    },
    defaultApiOptions,
  );

  if (authResponse?.success) {
    sendLoginEvent();
  }

  return authResponse;
};

type VerifyResponse = AuthClientResponse | AuthError | null | undefined;
/**
 * Checks if user is authenticated, then returns auth user from redis cache.
 */
export const verifyUser = async (): Promise<VerifyResponse> => {
  // TODO: Why is this a POST without any data? Could it not just be a get?
  return await httpPostSigned(authEndpoints.verify, {}, defaultApiOptions);
};

/**
 * Logs user out in Cognito, deletes redis cache & removes cookies from header.
 */
export const logoutUser = async () => {
  return await httpPostSigned(authEndpoints.logOut, {}, defaultApiOptions);
};

// TODO: merge the payloads
interface CreateUserPayload {
  email?: string;
  globalId?: string;
  password: string;
  postalCode?: string;
  username: string;
  app?: string | null;
  returnUrl?: string | null;
}

/**
 * Create the user with either the email, sending the user on the signup scenario, or globalId and postalCode
 * and stores initial auth user object in redis cache, and returns it. // TODO: Do we return AuthUserCacheAttributesWithNoTokens when created with GlobalId to log user in right away?
 */
export const createAccount = async (
  payload: CreateUserPayload,
): Promise<AuthResponse> => {
  const authResponse = await httpPostSigned<AuthResponse>(
    authEndpoints.create,
    {
      ...payload,
    },
    defaultApiOptions,
  );

  if (authResponse?.success && payload.globalId && payload.postalCode) {
    sendCreateAccountEvent();
  }

  return authResponse;
};

interface ConfirmCreateUserPayload {
  confirmationCode: string;
  authUsername: string;
}

/**
 * Verify the validation code when creating an account with an email address
 */
export const confirmAccountEmail = async (
  payload: ConfirmCreateUserPayload,
): Promise<AuthResponse> => {
  const confirmResponse = await httpPostSigned<AuthResponse>(
    authEndpoints.createValidate,
    {
      ...payload,
    },
    defaultApiOptions,
  );

  if (confirmResponse?.success) {
    sendCreateAccountEvent(true);
  }

  return confirmResponse;
};

interface ResendConfirmationEmailPayload {
  authUsername?: string;
  email?: string;
  app?: string;
  returnUrl?: string;
}

export interface ResendConfirmationSuccess {
  success: true;
  authUsername: string;
}

export type ResendConfirmationResponse =
  | ResendConfirmationSuccess
  | AuthError
  | null
  | undefined;

/**
 * Create the user with either the email, sending the user on the signup scenario, or globalId and postalCode
 * and stores initial auth user object in redis cache, and returns it.
 */
export const resendConfirmationEmail = async (
  payload: ResendConfirmationEmailPayload,
): Promise<ResendConfirmationResponse> => {
  return await httpPostSigned(
    authEndpoints.resendConfirmation,
    {
      ...payload,
    },
    defaultApiOptions,
  );
};

interface ForgotPasswordPayload {
  email: string;
  username?: string;
  globalId?: string;
  app?: string;
  returnUrl?: string;
}

export interface ForgotPasswordSuccess {
  success: true;
  authUsername: string;
}

export type ForgotResponse =
  | ForgotPasswordSuccess
  | AuthError
  | null
  | undefined;

/**
 * Initiate the password reset process
 */
export const forgotPassword = async (
  payload: ForgotPasswordPayload,
): Promise<ForgotResponse> => {
  return await httpPostSigned(
    authEndpoints.password,
    {
      ...payload,
    },
    defaultApiOptions,
  );
};

interface ForgotPasswordValidatePayload {
  authUsername: string;
  validationCode: string;
  password: string;
}

/**
 * Validate the confirmation code and reset the password
 */
export const forgotPasswordValidate = async (
  payload: ForgotPasswordValidatePayload,
): Promise<ForgotResponse> => {
  return await httpPostSigned(
    authEndpoints.passwordValidate,
    {
      ...payload,
    },
    defaultApiOptions,
  );
};

interface RecoverUsernamePayload {
  email: string;
  sponsorDonorNumber?: string;
}
/**
 * Create the user with either the email, sending the user on the signup scenario, or globalId and postalCode
 * and stores initial auth user object in redis cache, and returns it.
 */
export const recoverUsername = async (
  payload: RecoverUsernamePayload,
): Promise<AuthResponse> => {
  return await httpPostSigned(
    authEndpoints.username,
    {
      ...payload,
    },
    defaultApiOptions,
  );
};
